/**
 * OntologyImprovementsPanel - Main Improvements Tab Panel
 *
 * Combines Tier 1 structural analysis and Tier 2 semantic analysis
 * into a single panel for the OntologyBrowser Improvements tab.
 *
 * @version 1.0.0
 * @date 2026-02-04
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { StructuralHealthCard, type StructuralAnalysis } from './StructuralHealthCard';
import { DeepAnalysisCard, type SemanticAnalysis } from './DeepAnalysisCard';
import { ReportExportButton } from './ReportExportButton';
import './OntologyImprovementsPanel.css';

// =============================================================================
// TYPES
// =============================================================================

interface OntologyImprovementsPanelProps {
  ontologyId: string;
}

interface StructuralAnalysisResponse {
  ontologyId: string;
  ontologyName: string;
  namespace: string;
  version: string;
  analysis: StructuralAnalysis;
}

interface SemanticAnalysisResponse {
  ontologyId: string;
  ontologyName: string;
  namespace: string;
  version: string;
  analysis: SemanticAnalysis;
  usage: {
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
  };
}

interface StatusResponse {
  structuralAnalysisAvailable: boolean;
  semanticAnalysisAvailable: boolean;
  message: string;
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const OntologyImprovementsPanel: React.FC<OntologyImprovementsPanelProps> = ({
  ontologyId,
}) => {
  const { authFetch } = useApiClient();

  // Structural analysis state
  const [structuralAnalysis, setStructuralAnalysis] = useState<StructuralAnalysis | null>(null);
  const [structuralLoading, setStructuralLoading] = useState(false);
  const [structuralError, setStructuralError] = useState<string | null>(null);

  // Semantic analysis state
  const [semanticAnalysis, setSemanticAnalysis] = useState<SemanticAnalysis | null>(null);
  const [semanticLoading, setSemanticLoading] = useState(false);
  const [semanticError, setSemanticError] = useState<string | null>(null);
  const [semanticUsage, setSemanticUsage] = useState<{
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
  } | undefined>();

  // AI availability
  const [aiAvailable, setAiAvailable] = useState(true);

  // Playbook entries for "How to Fix" links
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- playbook entries are loosely typed from API
  const [playbookEntries, setPlaybookEntries] = useState<any[]>([]);

  // Check AI availability + fetch playbooks
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const response = await authFetch('/api/ontology-improvement/status');
        if (response.ok) {
          const data: StatusResponse = await response.json();
          setAiAvailable(data.semanticAnalysisAvailable);
        }
      } catch (err) {
        console.error('[OntologyImprovementsPanel] Status check failed:', err);
        setAiAvailable(false);
      }
    };

    const fetchPlaybooks = async () => {
      try {
        const response = await authFetch('/api/ontology-improvement/playbooks');
        if (response.ok) {
          const data = await response.json();
          setPlaybookEntries(data.playbooks || []);
        }
      } catch {
        // Non-fatal — playbooks are a nice-to-have enhancement
      }
    };

    checkStatus();
    fetchPlaybooks();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- authFetch is stable
  }, []);

  // Fetch structural analysis on mount
  useEffect(() => {
    if (!ontologyId) return;

    const fetchStructuralAnalysis = async () => {
      setStructuralLoading(true);
      setStructuralError(null);

      try {
        const response = await authFetch(
          `/api/ontology-improvement/${ontologyId}/structural`
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to fetch structural analysis');
        }

        const data: StructuralAnalysisResponse = await response.json();
        setStructuralAnalysis(data.analysis);
      } catch (err) {
        console.error('[OntologyImprovementsPanel] Structural analysis error:', err);
        setStructuralError(
          err instanceof Error ? err.message : 'Failed to load structural analysis'
        );
      } finally {
        setStructuralLoading(false);
      }
    };

    fetchStructuralAnalysis();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- authFetch is stable
  }, [ontologyId]);

  // Re-analyze structural analysis (user-triggered)
  const reanalyzeStructure = useCallback(async () => {
    if (!ontologyId) return;

    setStructuralLoading(true);
    setStructuralError(null);

    try {
      // Call reparse endpoint to re-analyze with updated rules
      const response = await authFetch(
        `/api/ontology-improvement/${ontologyId}/reparse`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to re-analyze ontology');
      }

      const data: StructuralAnalysisResponse = await response.json();
      setStructuralAnalysis(data.analysis);
    } catch (err) {
      console.error('[OntologyImprovementsPanel] Re-analysis error:', err);
      setStructuralError(
        err instanceof Error ? err.message : 'Failed to re-analyze ontology'
      );
    } finally {
      setStructuralLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- authFetch is stable
  }, [ontologyId]);

  // Run semantic analysis (user-triggered)
  const runSemanticAnalysis = useCallback(async () => {
    if (!ontologyId) return;

    setSemanticLoading(true);
    setSemanticError(null);
    setSemanticAnalysis(null);
    setSemanticUsage(undefined);

    try {
      const response = await authFetch(
        `/api/ontology-improvement/${ontologyId}/semantic`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to run semantic analysis');
      }

      const data: SemanticAnalysisResponse = await response.json();
      setSemanticAnalysis(data.analysis);
      setSemanticUsage(data.usage);
    } catch (err) {
      console.error('[OntologyImprovementsPanel] Semantic analysis error:', err);
      setSemanticError(
        err instanceof Error ? err.message : 'Failed to run semantic analysis'
      );
    } finally {
      setSemanticLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- authFetch is stable
  }, [ontologyId]);

  return (
    <div className="ontology-improvements-panel">
      {/* Panel Header with Export */}
      <div className="ontology-improvements-header">
        <h3 className="ontology-improvements-title">Ontology Analysis</h3>
        <ReportExportButton
          ontologyId={ontologyId}
          disabled={structuralLoading || !structuralAnalysis}
        />
      </div>

      {/* Tier 1: Structural Analysis */}
      <StructuralHealthCard
        analysis={structuralAnalysis}
        loading={structuralLoading}
        error={structuralError}
        onReanalyze={reanalyzeStructure}
        playbookEntries={playbookEntries}
      />

      {/* Tier 2: Semantic Analysis */}
      <DeepAnalysisCard
        analysis={semanticAnalysis}
        loading={semanticLoading}
        error={semanticError}
        onRunAnalysis={runSemanticAnalysis}
        aiAvailable={aiAvailable}
        usage={semanticUsage}
      />
    </div>
  );
};

export default OntologyImprovementsPanel;
